import { sharedReactorWebsocket as ws } from './reactor.js';
import { dashboardUserService } from './services/dashboarduserservice.js';
import { persistentStorage } from './persistentstorage.js';
import log from './log.js';
import { tinyCookie } from '../dependencies/tiny-cookie.js';

export default class ReactorAuthenticationHandler {
  constructor() {
    this.tenancyPromiseResolve;
    this.tenancyPromiseReject;
    this.tenancyPromise = new Promise((resolve, reject) => {
      this.tenancyPromiseResolve = resolve;
      this.tenancyPromiseReject = reject;
    });
    this.username = null;
    this.profile = null;
    this.authSessionId = null;
    this.tenancy = null;
    this.permissionsForTenancy = [];
    this.userAuthTypes = [];
    this.userCreatedDate = null;
    this.userPreviousLogin = null;
    this.authUserId = null;
    this.loginToken = null;
    this.regionWsUrl = null;
    this.sessionProperties = {};
    this.sessionExpiredCallback = null;
    this.loginCallback = null;
    this.publicAccessKey = null;
    this.loginPromiseResolve;
    this.loginPromiseReject;
    this.loginPromise = new Promise((resolve, reject) => {
      this.loginPromiseResolve = resolve;
      this.loginPromiseReject = reject;
    });
    this.readPersistentStorage();

    ws.addHandler("DashboardUserSessionHasExpired", msg => {
      log.info('Dashboard user session expired broadcasted');
      this.sessionExpired();
    });
  }

  sessionExpired() {

    let stored_session_id = persistentStorage.get("authSessionId");
    if (this.authSessionId !== stored_session_id && stored_session_id != null && stored_session_id.length > 0) {
      //session has been changed (user has re-logged in)
      let stored_auth_user_id = persistentStorage.get("authUserId");
      if (this.authUserId === stored_auth_user_id && stored_auth_user_id != null && stored_auth_user_id.length > 0) {
        //still same user but new session -> let's just resume that session instead of showing session expired.
        this.readPersistentStorage();
        this.resumeSession();
        return;
      } else {
        //user has changed, show session expiry which should reload the page completely
        //also make sure we've read in the latest from storage so we don't accidently end up here again
        this.readPersistentStorage();
      }
    } else {
      //only clear auth session if it hasn't changed to a new one
      this.clearAuthSession();
    }

    ws.clear();
    if (this.sessionExpiredCallback) {
      this.sessionExpiredCallback();
    }
  }

  setAuthSession(auth_session) {
    this.authSessionId = auth_session.sessionId;
    this.tenancy = auth_session.tenancy;
    persistentStorage.set("authSessionId", this.authSessionId);
    persistentStorage.set("authSessionTenancy", this.tenancy);
    if (auth_session.userName) {
      this.username = auth_session.userName;
      persistentStorage.set("authSessionUsername", this.username);
    }
    if (auth_session.profile) {
      this.profile = auth_session.profile;
      persistentStorage.set("authProfile", this.profile);
    }
    this.tenancyPromiseResolve(this.tenancy);
  }

  getAuthSession() {
    return {
      sessionId: this.authSessionId,
      tenancy: this.tenancy,
      username: this.username,
      profile: this.profile
    };
  }

  setLoginToken(loginToken) {
    this.loginToken = loginToken;
    persistentStorage.set("loginToken", loginToken);
  }

  setRegionUrl(regionUrl) {
    if (!regionUrl) {
      regionUrl = "";
    } else if (regionUrl.lastIndexOf("/api") == -1) {
      regionUrl += "/api";
    }
    //regionUrl contains http, convert to ws
    this.regionWsUrl = regionUrl.replace("http://", "ws://").replace("https://", "wss://");
    persistentStorage.set("regionWsUrl", this.regionWsUrl);
  }

  doRegionAwareConnect(defaultUrl) {
    if (this.regionWsUrl) {
      if (ws.URL() != this.regionWsUrl) {
        console.log("Connecting to " + this.regionWsUrl);
        ws.disconnect();
        ws.connect(this.regionWsUrl);
      } else {
        console.log("doRegionAwareConnect: connect to same url (" + this.regionWsUrl + "), do nothing");
      }
    } else if (defaultUrl) {
      if (ws.URL() != defaultUrl) {
        console.log("Connecting to " + defaultUrl);
        ws.disconnect();
        ws.connect(defaultUrl);
      } else {
        console.log("doRegionAwareConnect: connect to same url (" + defaultUrl + "), do nothing");
      }
    } else {
      console.log("Could not connect websocket, regional and default url missing");
    }
  }

  readPersistentStorage() {
    let auth_session_id = persistentStorage.get("authSessionId");
    let auth_tenancy = persistentStorage.get("authSessionTenancy");
    //    var auth_permissions_for_tenancy = persistentStorage.get("permissionsForTenancy");
    let user_auth_types = persistentStorage.get("userAuthTypes");
    let user_created_date = persistentStorage.get("userCreatedDate");
    let user_previous_login = persistentStorage.get("userPreviousLogin");
    let auth_username = persistentStorage.get("authSessionUsername");
    let auth_profile = persistentStorage.get("authProfile");
    let session_properties = persistentStorage.get("sessionProperties");

    if (auth_session_id && auth_tenancy) {
      log.info("Got auth parameters, session=" + auth_session_id + ", tenancy=" + auth_tenancy);
      this.authSessionId = auth_session_id;
      this.tenancy = auth_tenancy;
      this.username = auth_username;
      if (auth_profile) {
        this.profile = auth_profile;
      }
      //      this.permissionsForTenancy = auth_permissions_for_tenancy;
      this.userAuthTypes = user_auth_types;
      this.userCreatedDate = user_created_date;
      this.userPreviousLogin = user_previous_login;
      if (session_properties != "" && session_properties != null) {
        try {
          this.sessionProperties = JSON.parse(session_properties);
        } catch (error) {
          console.log("Could not parse session_properties");
        };
      }
      this.tenancyPromiseResolve(this.tenancy);
    }
    let login_token = persistentStorage.get("loginToken");
    if (login_token) {
      this.loginToken = login_token;
    }
    let region_url = persistentStorage.get("regionWsUrl");
    if (region_url) {
      this.regionWsUrl = region_url;
    }
  }

  login(username, password, lockedOutUrl, tenancyUrl, onComplete) {
    this.loginWithCredentials(dashboardUserService.getEmailAndPasswordCredentials(username, password, lockedOutUrl), tenancyUrl, onComplete);
  }

  loginWithMFA(username, password, mfa_code, lockedOutUrl, tenancyUrl, onComplete) {
    this.loginWithCredentials(dashboardUserService.getEmailPasswordAndMFACredentials(username, password, mfa_code, lockedOutUrl), tenancyUrl, onComplete);
  }

  loginWithToken(token, tenancyUrl, onComplete) {
    this.loginWithCredentials(dashboardUserService.getLoginTokenCredentials(token), tenancyUrl, onComplete);
  }

  loginWithTokenAndMFA(token, mfa_code, tenancyUrl, onComplete) {
    this.loginWithCredentials(dashboardUserService.getLoginTokenAndMFACredentials(token, mfa_code), tenancyUrl, onComplete);
  }

  loginWithCredentials(credentials, tenancyUrl, onComplete) {
    if (this.isAuthenticated()) {
      this.logout(function () {});
    }
    var complete = onComplete;
    dashboardUserService.loginToTenancyByURL(credentials, tenancyUrl, response => {
      if (response.exceptionName != null) {
        complete(response);
      } else {
        persistentStorage.setUseSessionStorage(response.data.useSessionStorage);

        this.authSessionId = response.data.sessionId;
        this.tenancy = response.data.tenancy;
        persistentStorage.set("authSessionId", this.authSessionId);
        persistentStorage.set("authSessionTenancy", this.tenancy);
        if (response.data.userName) {
          this.username = response.data.userName;
          persistentStorage.set("authSessionUsername", this.username);
        }
        if (response.data.profile) {
          this.profile = response.data.profile;
          persistentStorage.set("authProfile", this.profile);
        }
        ws.resumeSession(this.authSessionId, this.tenancy.tenancyId, response => {
          this.permissionsForTenancy = response.data.authorizationInfo.userPermissions;
          //          persistentStorage.set("permissionsForTenancy", this.permissionsForTenancy);
          this.userAuthTypes = response.data.authorizationInfo.userAuthTypes;
          this.userCreatedDate = response.data.authorizationInfo.userCreatedDate;
          this.userPreviousLogin = response.data.authorizationInfo.userPreviousLogin;
          this.sessionAuthType = response.data.authorizationInfo.sessionAuthType;
          persistentStorage.set("userAuthTypes", this.userAuthTypes);
          persistentStorage.set("userCreatedDate", this.userCreatedDate);
          persistentStorage.set("userPreviousLogin", this.userPreviousLogin);
          this.authUserId = response.data.authorizationInfo.authUserId;
          persistentStorage.set("authUserId", this.authUserId);
          complete(response);
          if (this.loginCallback) {
            this.loginCallback();
          }

          this.loginPromiseResolve({
            dashboardUserId: this.authUserId,
            tenancyId: this.tenancy.tenancyId
          });

          this.tenancyPromiseResolve(this.tenancy);
        });
      }
    }).catch(failure => {
      if (failure != null && failure.failure != null && failure.failure.response != null) {
        if (failure.failure.response.exceptionName === "MFARequiredException") {
          failure.remove();
        }
      }
    });
  }

  loginGetTenanciesWithAuthCode(auth_type, auth_code, app_id, on_complete) {
    this.loginGetTenanciesWithCredentialsAndAppId(dashboardUserService.getOAuthCredentials(auth_type, auth_code), app_id, on_complete);
  }

  loginGetTenancies(username, password, lockedOutUrl, app_id, onComplete) {
    this.loginGetTenanciesWithCredentialsAndAppId(dashboardUserService.getEmailAndPasswordCredentials(username, password, lockedOutUrl), app_id, onComplete);
  }

  loginGetTenanciesWithToken(token, app_id, onComplete) {
    this.loginGetTenanciesWithCredentialsAndAppId(dashboardUserService.getLoginTokenCredentials(token), app_id, onComplete);
  }

  loginGetTenanciesWithCredentialsAndAppId(credentials, app_id, onComplete) {
    if (this.isAuthenticated()) {
      this.logout(function () {});
    }
    dashboardUserService.loginAndGetTenanciesWithCredentialsAndAppId(credentials, app_id, response => {
      if (response.exceptionName != null) {
        onComplete(response);
      } else {
        this.loginToken = response.data.loginToken;
        persistentStorage.set("loginToken", response.data.loginToken);
        onComplete(response);
      }
    });
  }

  loginToTenancyByToken(token, onComplete) {
    if (this.isAuthenticated()) {
      this.logout(function () {});
    }
    var complete = onComplete;
    dashboardUserService.loginToTenancyByToken(token, response => {
      if (response.exceptionName != null) {
        onComplete(response);
      } else {
        persistentStorage.set("loginToken", token);
        this.authSessionId = response.data.sessionId;
        this.tenancy = response.data.tenancy;
        persistentStorage.set("authSessionId", this.authSessionId);
        persistentStorage.set("authSessionTenancy", this.tenancy);
        if (response.data.userName) {
          this.username = response.data.userName;
          persistentStorage.set("authSessionUsername", this.username);
        }
        if (response.data.profile) {
          this.profile = response.data.profile;
          persistentStorage.set("authProfile", this.profile);
        }
        this.resumeSession(() => {
          complete(response);
          if (this.loginCallback) {
            this.loginCallback();
          }
          this.loginPromiseResolve({
            dashboardUserId: this.authUserId,
            tenancyId: this.tenancy.tenancyId
          });
          this.tenancyPromiseResolve(this.tenancy);
        });
      }
    });
  }

  logout(onComplete) {
    // call callback even if not authenticated to avoid waiting on response forever
    if (!this.isAuthenticated()) {
      this.clearAuthSession();
      onComplete({});
      return;
    }
    this.clearAuthSession();
    //unsubscribe from anything that has not been explicitly unsubscribed
    ws.unsubscribeAllTopics();
    ws.sendServiceRequest("httpapiservice-1_0", "LogoutRequest", {}, response => {
      log.info('Logout Response: ' + JSON.stringify(response));
      onComplete(response);
    });
  }

  getAuthSessionId() {
    return this.authSessionId;
  }

  getAuthTenancy() {
    return this.tenancy;
  }

  getAuthPermissions() {
    return this.permissionsForTenancy;
  }

  getUserAuthTypes() {
    return this.userAuthTypes;
  }

  getUserCreatedDate() {
    return this.userCreatedDate;
  }

  getUserPreviousLogin() {
    return this.userPreviousLogin;
  }

  getUserSessionAuthType() {
    return this.sessionAuthType;
  }

  getAuthUserId() {
    return this.authUserId;
  }

  getAuthUserName() {
    if (this.username == null) return " "; //there are empty profiles where username might be null, but session transfer doesn't support null
    return this.username;
  }

  getAuthProfile() {
    return this.profile;
  }

  getSessionProperties() {
    return this.sessionProperties;
  }

  refreshProfile() {
    return dashboardUserService.getOwnProfile().then(response => {
      if (response) {
        this.profile = response.profile;
        persistentStorage.set("authProfile", this.profile);
      }
      return true;
    }, failure => {
      return true;
    });
  }

  getLoginToken() {
    return this.loginToken;
  }

  clearAuthSession() {
    this.authSessionId = null;
    this.tenancy = null;
    this.permissionsForTenancy = [];
    this.userAuthTypes = [];
    this.userCreatedDate = null;
    this.userPreviousLogin = null;
    this.authUserId = null;
    this.username = null;
    this.profile = null;
    this.loginToken = null;
    this.regionWsUrl = null;
    this.sessionProperties = {};
    persistentStorage.set("authSessionId", "");
    persistentStorage.set("authSessionTenancy", "");
    //    persistentStorage.set("permissionsForTenancy", "");
    persistentStorage.set("userAuthTypes", "");
    persistentStorage.set("userCreatedDate", "");
    persistentStorage.set("userPreviousLogin", "");
    persistentStorage.set("authSessionUsername", "");
    persistentStorage.set("authProfile", "");
    persistentStorage.set("loginToken", "");
    persistentStorage.set("regionWsUrl", "");
    persistentStorage.set("sessionProperties", "");
    persistentStorage.set("authUserId", "");

    //Only needed if running both old and new dashboard on same machine, ex. localhost
    tinyCookie.remove('PLAY_SESSION');

    //setUseSessionStorage MUST BE the last one to be set on persistent storage, otherwise the session might not be removed properly
    persistentStorage.setUseSessionStorage(false);
  }

  isAuthenticated() {
    return !(this.authSessionId == undefined || this.authSessionId == null || this.authSessionId == '');
  }

  hasLoginToken() {
    return !(this.loginToken == undefined || this.loginToken == null || this.loginToken == '');
  }

  resumeSession(callback) {
    if (this.getAuthSessionId() != null && this.getAuthTenancy() != null) {
      ws.resumeSession(this.getAuthSessionId(), this.getAuthTenancy().tenancyId, response => {
        if (response.status === 'success') {
          if (response.data.authorizationInfo == null) {
            log.info('Dashboard user session expired');
            this.sessionExpired();
            return;
          }

          log.info('Dashboard user session resumed');
          this.permissionsForTenancy = response.data.authorizationInfo.userPermissions;
          //          persistentStorage.set("permissionsForTenancy", this.permissionsForTenancy);
          this.userAuthTypes = response.data.authorizationInfo.userAuthTypes;
          this.userCreatedDate = response.data.authorizationInfo.userCreatedDate;
          this.userPreviousLogin = response.data.authorizationInfo.userPreviousLogin;
          this.sessionAuthType = response.data.authorizationInfo.sessionAuthType;
          this.authUserId = response.data.authorizationInfo.authUserId;
          persistentStorage.set("authUserId", this.authUserId);
          persistentStorage.set("userAuthTypes", this.userAuthTypes);
          persistentStorage.set("userCreatedDate", this.userCreatedDate);
          persistentStorage.set("userPreviousLogin", this.userPreviousLogin);
          if (typeof callback === 'function') callback();

          this.loginPromiseResolve({
            dashboardUserId: this.authUserId,
            tenancyId: this.tenancy.tenancyId
          });
        }
      });
    } else {
      log.info('Websocket not authenticated, calling callback...');
      if (typeof callback === 'function') callback();
    }
  }

  setupPublicAccess(key, callback) {
    this.publicAccessKey = key;
    if (this.publicAccessKey) {
      let props = {
        'publicAccessKey': this.publicAccessKey,
        'publicAccessKeyPassword': null
      };
      ws.setConnectionInfo(props, response => {
        if (response.status == 'success') {
          var msg = {
            service: "httpapiservice-1_0",
            message: "AuthorizationInfoRequest",
            data: {}
          };
          this.tenancy = { tenancyId: response.data.tenancyId, appId: "", tenancyName: "", tenancyURL: "" };
          ws.sendServiceRequest(msg.service, msg.message, msg.data, response => {
            if (response.data && response.data.authorizationInfo) {
              let has_authenticated_permissions = this.authUserId && this.permissionsForTenancy.length > 0;
              if (!has_authenticated_permissions) {
                this.permissionsForTenancy = response.data.authorizationInfo.userPermissions;
              }
              if (typeof callback === 'function') {
                callback();
              }
              this.tenancyPromiseResolve(this.tenancy);
            }
          });
        } else {
          console.log('setConnectionInfo failed');
        }
      });
    } else {
      if (typeof callback === 'function') {
        callback();
      }
    }
  }

  setExpiredCallback(session_expired_callback) {
    this.sessionExpiredCallback = session_expired_callback;
  }

  setLoginCallback(callback) {
    this.loginCallback = callback;
  }

  setSessionProperties(key, value) {
    this.sessionProperties[key] = value;
    persistentStorage.set('sessionProperties', JSON.stringify(this.sessionProperties));
  }
}

export var sharedReactorAuthentication = new ReactorAuthenticationHandler();


//////////////////
// WEBPACK FOOTER
// ../ReactorJSCore/dist/authentication.js
// module id = psLG
// module chunks = 1 2 3 4 5 6